import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Inbox, Mail, Loader2, Search, RefreshCw, Send, Sparkles, MessageSquareReply,
  AlertCircle, Filter, Paperclip, ExternalLink, CalendarPlus, ListChecks, CheckSquare, Square,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PreguntarBuzon from '../components/PreguntarBuzon';
import EmailViewer from '../components/EmailViewer';
import RecipientInput from '../components/correo/RecipientInput';
import ComposerAiToolbar from '../components/correo/ComposerAiToolbar';
import AttachmentsInput from '../components/correo/AttachmentsInput';
import { req } from '../services/api';


const TIPO_BADGE = {
  microsoft: 'bg-blue-50 text-blue-700 border-blue-200',
  google: 'bg-rose-50 text-rose-700 border-rose-200',
  imap: 'bg-ink-100 text-ink-700 border-ink-200',
};

export default function CorreosClassic() {
  const navigate = useNavigate();
  const [cuentas, setCuentas] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroCuenta, setFiltroCuenta] = useState('all');
  const [soloNoLeidos, setSoloNoLeidos] = useState(false);
  const [soloOportunidades, setSoloOportunidades] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaTerm, setBusquedaTerm] = useState('');
  const [composer, setComposer] = useState({ open: false, to: [], cc: [], bcc: [], showCc: false, showBcc: false, subject: '', body: '', cuenta_id: '', adjuntos: [] });
  const [sincronizando, setSincronizando] = useState(false);
  const [verMensaje, setVerMensaje] = useState(null);
  const [sugerencias, setSugerencias] = useState({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] });
  const [tareasIA, setTareasIA] = useState({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false });

  const cargarCuentas = async () => {
    try {
      const c = await req('/api/correo/cuentas');
      setCuentas(Array.isArray(c) ? c : []);
    } catch (e) { toast.error(`Cuentas: ${e.message}`); setCuentas([]); }
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '100');
      if (filtroCuenta !== 'all') params.set('cuenta_id', filtroCuenta);
      if (soloNoLeidos) params.set('solo_no_leidos', 'true');
      if (busquedaTerm.trim()) params.set('q', busquedaTerm.trim());
      const r = await req(`/api/correo/sincronizados?${params.toString()}`);
      setItems(r.items || []);
    } catch (e) { toast.error(e.message); setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargarCuentas(); }, []);
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [filtroCuenta, soloNoLeidos, busquedaTerm]);

  const filtered = useMemo(() => (
    soloOportunidades ? items.filter((m) => m.es_oportunidad) : items
  ), [items, soloOportunidades]);
  const oportunidadesCount = useMemo(
    () => items.filter((m) => m.es_oportunidad).length,
    [items],
  );

  const sincronizarTodas = async () => {
    if (cuentas.length === 0) return;
    setSincronizando(true);
    let ok = 0, fail = 0, totalNuevos = 0;
    for (const c of cuentas) {
      try {
        // Usa el endpoint de sincronización avanzada con sync_config persistido
        // de la cuenta (o defaults conservadores 90d/500/inbox).
        const r = await req(`/api/correo/cuentas/${c.id}/sincronizar`, {
          method: 'POST', body: JSON.stringify({}),
        });
        ok++;
        totalNuevos += r.total_persistidos || 0;
      } catch (e) { fail++; console.error('sync cuenta', e); }
    }
    setSincronizando(false);
    if (ok > 0) toast.success(`${ok} cuenta(s) · ${totalNuevos} mensaje(s) nuevos${fail ? ` · ${fail} fallaron` : ''}`);
    if (ok === 0 && fail > 0) toast.error('No se pudo sincronizar ninguna cuenta');
    cargar();
  };

  const cuentasMap = useMemo(() => {
    const m = {};
    cuentas.forEach((c) => { m[c.id] = c; });
    return m;
  }, [cuentas]);

  const aplicarBusqueda = () => setBusquedaTerm(busqueda);

  const responder = (m, bodyTexto) => {
    const cuenta = cuentasMap[m.cuenta_id];
    // Usa el cuerpo completo si está disponible, sino el preview como fallback
    const cuerpoOriginal = (bodyTexto && bodyTexto.trim()) ? bodyTexto : (m.preview || '');
    setComposer({
      open: true,
      to: m.from_address ? [m.from_address] : [],
      cc: [], bcc: [], showCc: false, showBcc: false,
      subject: m.subject?.startsWith('Re:') ? m.subject : `Re: ${m.subject || ''}`,
      body: `\n\n--- Mensaje original ---\nDe: ${m.from_name || m.from_address}\nAsunto: ${m.subject || ''}\n\n${cuerpoOriginal}`,
      cuenta_id: cuenta?.id || cuentas[0]?.id || '',
      adjuntos: [],
    });
    setVerMensaje(null);
  };

  const abrirSugerencias = async (m) => {
    setVerMensaje(null);
    setSugerencias({ open: true, loading: true, msg: m, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] });
    try {
      const r = await req('/api/correo/ai/sugerir-respuestas', {
        method: 'POST', body: JSON.stringify({ message_id: m.message_id }),
      });
      setSugerencias({
        open: true, loading: false, msg: m,
        borradores: r.borradores || [],
        perfilAplicado: !!r.perfil_aplicado,
        contexto: r.contexto || null,
        memoriasRelevantes: r.memorias_relevantes || [],
      });
    } catch (e) {
      toast.error(`Gemini: ${e.message}`);
      setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] });
    }
  };

  const usarBorrador = (b) => {
    const m = sugerencias.msg;
    setComposer({
      open: true,
      to: m?.from_address ? [m.from_address] : [],
      cc: [], bcc: [], showCc: false, showBcc: false,
      subject: b.asunto || (m?.subject?.startsWith('Re:') ? m.subject : `Re: ${m?.subject || ''}`),
      body: b.cuerpo || '',
      cuenta_id: cuentasMap[m?.cuenta_id]?.id || cuentas[0]?.id || '',
      adjuntos: [],
    });
    setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] });
  };

  // ── Modal selección de tareas extraídas (Fix 1) ────────────────
  const cerrarTareasIA = () => setTareasIA({ open: false, loading: false, msg: null, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false });

  const toggleTareaSel = (i) => {
    setTareasIA((t) => {
      const sel = new Set(t.seleccionadas);
      if (sel.has(i)) sel.delete(i); else sel.add(i);
      return { ...t, seleccionadas: sel };
    });
  };

  const extraerTareas = async (m) => {
    setVerMensaje(null);
    setTareasIA({ open: true, loading: true, msg: m, propuestas: [], seleccionadas: new Set(), resumen: '', creando: false });
    try {
      const r = await req('/api/correo/ai/extraer-tareas', {
        method: 'POST', body: JSON.stringify({ message_id: m.message_id }),
      });
      const propuestas = r.tareas_propuestas || [];
      const sel = new Set(propuestas.map((_, i) => i));
      setTareasIA({ open: true, loading: false, msg: m, propuestas, seleccionadas: sel, resumen: r.resumen_correo || '', creando: false });
    } catch (e) {
      toast.error(`IA: ${e.message}`);
      cerrarTareasIA();
    }
  };

  const crearTareasSeleccionadas = async () => {
    const m = tareasIA.msg;
    const eid = m?.vinculado_entidad_id || null;
    const cid = m?.vinculado_contacto_id || null;
    setTareasIA((t) => ({ ...t, creando: true }));
    let creadas = 0; let fallos = 0;
    for (let i = 0; i < tareasIA.propuestas.length; i++) {
      if (!tareasIA.seleccionadas.has(i)) continue;
      const t = tareasIA.propuestas[i];
      try {
        await req('/api/tareas', {
          method: 'POST',
          body: JSON.stringify({
            titulo: t.titulo,
            descripcion: (t.descripcion || '') + (m?.subject ? `\n\n📧 Origen: correo "${m.subject}"` : ''),
            entidad_id: eid,
            contacto_id: cid,
            vencimiento: t.fecha_propuesta || t.vencimiento || null,
            prioridad: t.prioridad || 'media',
            estado: 'pendiente',
          }),
        });
        creadas++;
      } catch (e) { fallos++; console.error('crear tarea', e); }
    }
    if (creadas > 0) toast.success(`${creadas} tarea(s) creada(s)${fallos ? ` · ${fallos} fallaron` : ''}`);
    if (creadas === 0 && fallos > 0) toast.error(`No se pudo crear ninguna tarea (${fallos} fallos)`);
    cerrarTareasIA();
  };

  const crearVisita = (m) => {
    // Redirige a /visitas con prefilled vía query string. La página lo lee.
    const params = new URLSearchParams({
      desde_correo: '1',
      message_id: m.message_id,
      contacto_email: m.from_address || '',
      titulo: `Visita seguimiento: ${m.subject || ''}`.slice(0, 200),
    });
    navigate(`/visitas?${params.toString()}`);
  };

  const enviar = async () => {
    if (!composer.cuenta_id) { toast.error('Selecciona una cuenta de envío'); return; }
    if (composer.to.length === 0) { toast.error('Añade al menos un destinatario'); return; }
    try {
      const fd = new FormData();
      fd.append('to', JSON.stringify(composer.to));
      fd.append('subject', composer.subject);
      fd.append('body', composer.body);
      if (composer.cc.length > 0) fd.append('cc', JSON.stringify(composer.cc));
      if (composer.bcc.length > 0) fd.append('bcc', JSON.stringify(composer.bcc));
      for (const f of (composer.adjuntos || [])) fd.append('attachments', f, f.name);
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/correo/cuentas/${composer.cuenta_id}/enviar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      const ad = composer.adjuntos?.length || 0;
      toast.success(`Correo enviado${ad ? ` con ${ad} adjunto(s)` : ''}${data.vinculado_entidad_id ? ' · vinculado a empresa' : ''}`);
      setComposer({ open: false, to: [], cc: [], bcc: [], showCc: false, showBcc: false, subject: '', body: '', cuenta_id: '', adjuntos: [] });
      setTimeout(cargar, 800);
    } catch (e) { toast.error(`Envío: ${e.message}`); }
  };

  if (cuentas.length === 0 && !loading) {
    return (
      <div className="max-w-5xl mx-auto p-4 md:p-6" data-testid="correos-page">
        <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight flex items-center gap-2 mb-4">
          <Inbox className="w-7 h-7 text-brand-500" /> Bandeja unificada
        </h1>
        <Card className="p-8 border-dashed border-amber-300 bg-amber-50/40 text-center" data-testid="correos-sin-cuentas">
          <AlertCircle className="w-10 h-10 text-amber-600 mx-auto mb-3" />
          <h3 className="font-semibold text-ink-900 mb-1">Sin cuentas de correo conectadas</h3>
          <p className="text-sm text-ink-600 mb-4">
            Para ver tu bandeja unificada aquí, primero conecta una cuenta (Microsoft 365, Gmail o IMAP).
          </p>
          <Button onClick={() => navigate('/perfil')} className="bg-brand-600 hover:bg-brand-700">
            <ExternalLink className="w-4 h-4 mr-1.5" /> Ir a Mi perfil
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4" data-testid="correos-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight flex items-center gap-2">
            <Inbox className="w-7 h-7 text-brand-500" /> Bandeja unificada
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Todos tus correos sincronizados en un solo sitio · {cuentas.length} cuenta(s)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PreguntarBuzon />
          <Button size="sm" variant="outline" onClick={sincronizarTodas} disabled={sincronizando} data-testid="correos-sync-todas">
            {sincronizando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Sincronizar
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/correos/enviados')} data-testid="correos-ver-enviados">
            <Send className="w-4 h-4 mr-1.5" /> Enviados
          </Button>
          <Button size="sm" onClick={() => setComposer({ open: true, to: [], cc: [], bcc: [], showCc: false, showBcc: false, subject: '', body: '', cuenta_id: cuentas[0]?.id || '', adjuntos: [] })} data-testid="correos-nuevo">
            <Send className="w-4 h-4 mr-1.5" /> Nuevo correo
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="p-3" data-testid="correos-filtros">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-ink-500">
            <Filter className="w-3.5 h-3.5" /> Filtros
          </div>

          <select
            value={filtroCuenta}
            onChange={(e) => setFiltroCuenta(e.target.value)}
            className="border border-ink-200 rounded px-2 py-1.5 text-sm bg-white"
            data-testid="filtro-cuenta"
          >
            <option value="all">Todas las cuentas</option>
            {cuentas.map((c) => (
              <option key={c.id} value={c.id}>{c.email} ({c.tipo})</option>
            ))}
          </select>

          <label className="flex items-center gap-1.5 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={soloNoLeidos}
              onChange={(e) => setSoloNoLeidos(e.target.checked)}
              data-testid="filtro-no-leidos"
            />
            Solo no leídos
          </label>

          <label className={`flex items-center gap-1.5 text-sm ${oportunidadesCount > 0 ? 'text-amber-800' : 'text-ink-500'}`}>
            <input
              type="checkbox"
              checked={soloOportunidades}
              onChange={(e) => setSoloOportunidades(e.target.checked)}
              data-testid="filtro-oportunidades"
              disabled={oportunidadesCount === 0}
            />
            🎯 Solo oportunidades {oportunidadesCount > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] border ml-1">{oportunidadesCount}</Badge>}
          </label>

          <div className="flex items-center gap-1 ml-auto">
            <Input
              type="search"
              placeholder="Buscar en asunto/preview…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') aplicarBusqueda(); }}
              className="text-sm w-64"
              data-testid="filtro-busqueda"
            />
            <Button size="sm" variant="outline" onClick={aplicarBusqueda} data-testid="filtro-buscar">
              <Search className="w-4 h-4" />
            </Button>
            {busquedaTerm && (
              <Button size="sm" variant="ghost" onClick={() => { setBusqueda(''); setBusquedaTerm(''); }}>✕</Button>
            )}
          </div>
        </div>
      </Card>

      {loading && (
        <div className="text-center py-12 text-ink-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" />Cargando correos…</div>
      )}

      {!loading && filtered.length === 0 && (
        <Card className="p-8 text-center border-dashed text-sm text-ink-500" data-testid="correos-vacio">
          {soloOportunidades && oportunidadesCount === 0
            ? 'No hay correos marcados como oportunidad. El detector se ejecuta cada 30 min.'
            : busquedaTerm || soloNoLeidos || filtroCuenta !== 'all'
              ? 'No hay correos que coincidan con los filtros aplicados.'
              : 'No hay correos sincronizados todavía. Pulsa "Sincronizar" arriba.'}
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-2" data-testid="correos-lista">
          {filtered.map((m) => {
            const cuenta = cuentasMap[m.cuenta_id];
            return (
              <Card
                key={m.message_id}
                className={`p-3 transition hover:border-brand-300 cursor-pointer ${!m.is_read ? 'border-l-4 border-l-blue-500 bg-blue-50/20' : ''}`}
                data-testid={`correo-item-${m.message_id}`}
                onClick={() => setVerMensaje(m)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs text-ink-500 mb-1 flex-wrap">
                      {cuenta && (
                        <Badge className={`text-[9px] uppercase border ${TIPO_BADGE[cuenta.tipo] || ''}`}>
                          {cuenta.email}
                        </Badge>
                      )}
                      <span>{m.received_at ? new Date(m.received_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : ''}</span>
                      {m.has_attachments && <Paperclip className="w-3 h-3 text-amber-600" title="Tiene adjuntos" />}
                      {!m.is_read && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[9px] border">No leído</Badge>}
                      {m.vinculado_entidad_id && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] border">↳ vinculado a empresa</Badge>
                      )}
                      {m.es_oportunidad && (
                        <Badge
                          className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] border font-bold"
                          data-testid={`badge-oportunidad-${m.message_id}`}
                          title={`Oportunidad detectada (similitud ${m.oportunidad_score ? (m.oportunidad_score * 100).toFixed(0) + '%' : '—'}). Configurable en /perfil → Preferencias IA.`}
                        >
                          🎯 Oportunidad
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-ink-900 truncate">{m.subject || '(sin asunto)'}</div>
                    <div className="text-xs text-ink-600 mt-0.5">
                      <span className="font-medium">De:</span> {m.from_name ? `${m.from_name} <${m.from_address}>` : m.from_address}
                    </div>
                    {m.preview && <div className="text-xs text-ink-500 mt-1.5 line-clamp-2">{m.preview}</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => extraerTareas(m)} title="Extraer tareas con IA (Gemini)" data-testid={`ia-extraer-${m.message_id}`} className="text-violet-700 hover:bg-violet-50">
                      <Sparkles className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => abrirSugerencias(m)} title="Sugerir respuestas con IA" data-testid={`ia-sugerir-${m.message_id}`} className="text-fuchsia-700 hover:bg-fuchsia-50">
                      <MessageSquareReply className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => crearVisita(m)} title="Crear visita desde correo" data-testid={`ia-visita-${m.message_id}`} className="text-emerald-700 hover:bg-emerald-50">
                      <CalendarPlus className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => responder(m)} data-testid={`responder-${m.message_id}`}>
                      <Send className="w-4 h-4 mr-1" /> Responder
                    </Button>
                    {m.vinculado_entidad_id && (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/clientes/${m.vinculado_entidad_id}`)} title="Abrir empresa vinculada" data-testid={`ir-empresa-${m.message_id}`}>
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {composer.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setComposer({ ...composer, open: false }); }}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="correos-composer">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2"><Send className="w-5 h-5" /> Nuevo correo</h3>
            <div className="space-y-3">
              {cuentas.length > 1 && (
                <div>
                  <label className="text-xs text-ink-500">Desde</label>
                  <select className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm bg-white" value={composer.cuenta_id} onChange={(e) => setComposer({ ...composer, cuenta_id: e.target.value })} data-testid="composer-cuenta">
                    {cuentas.map((c) => <option key={c.id} value={c.id}>{c.email} ({c.tipo})</option>)}
                  </select>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-ink-500">Para</label>
                  <div className="flex items-center gap-1">
                    {!composer.showCc && (
                      <button type="button" onClick={() => setComposer({ ...composer, showCc: true })} className="text-[11px] text-brand-600 hover:underline" data-testid="composer-add-cc">+ CC</button>
                    )}
                    {!composer.showBcc && (
                      <button type="button" onClick={() => setComposer({ ...composer, showBcc: true })} className="text-[11px] text-brand-600 hover:underline" data-testid="composer-add-bcc">+ CCO</button>
                    )}
                  </div>
                </div>
                <RecipientInput
                  value={composer.to}
                  onChange={(v) => setComposer({ ...composer, to: v })}
                  placeholder="nombre o email…"
                  testid="composer-to"
                />
              </div>
              {composer.showCc && (
                <div>
                  <label className="text-xs text-ink-500">CC</label>
                  <RecipientInput
                    value={composer.cc}
                    onChange={(v) => setComposer({ ...composer, cc: v })}
                    placeholder="copia (CC)…"
                    testid="composer-cc"
                  />
                </div>
              )}
              {composer.showBcc && (
                <div>
                  <label className="text-xs text-ink-500">CCO (oculta)</label>
                  <RecipientInput
                    value={composer.bcc}
                    onChange={(v) => setComposer({ ...composer, bcc: v })}
                    placeholder="copia oculta (CCO)…"
                    testid="composer-bcc"
                  />
                </div>
              )}
              <div>
                <label className="text-xs text-ink-500">Asunto</label>
                <Input value={composer.subject} onChange={(e) => setComposer({ ...composer, subject: e.target.value })} data-testid="composer-subject" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-ink-500">Mensaje</label>
                  <ComposerAiToolbar body={composer.body} onChange={(nb) => setComposer((c) => ({ ...c, body: nb }))} />
                </div>
                <textarea rows={10} className="w-full mt-1 border border-ink-200 rounded px-3 py-2 text-sm" value={composer.body} onChange={(e) => setComposer({ ...composer, body: e.target.value })} data-testid="composer-body" />
                <p className="text-[10px] text-ink-400 mt-1">La IA usa tu estilo aprendido (si está disponible) y nunca añade firmas que no sean tuyas.</p>
              </div>
              <div>
                <label className="text-xs text-ink-500 block mb-1">Adjuntos</label>
                <AttachmentsInput
                  value={composer.adjuntos || []}
                  onChange={(v) => setComposer((c) => ({ ...c, adjuntos: v }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setComposer({ ...composer, open: false })}>Cancelar</Button>
              <Button onClick={enviar} disabled={composer.to.length === 0 || !composer.subject || !composer.cuenta_id} data-testid="composer-enviar">
                <Send className="w-4 h-4 mr-1.5" /> Enviar
              </Button>
            </div>
          </Card>
        </div>
      )}
      {verMensaje && (
        <EmailViewer
          mensaje={verMensaje}
          onClose={() => setVerMensaje(null)}
          onResponder={responder}
          onExtraerTareas={extraerTareas}
          onSugerir={abrirSugerencias}
          onCrearVisita={crearVisita}
        />
      )}

      {sugerencias.open && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[55] p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] }); }}
        >
          <Card className="max-w-3xl w-full max-h-[85vh] overflow-y-auto p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="dialog-sugerencias-correos">
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <MessageSquareReply className="w-5 h-5 text-fuchsia-600" /> Borradores de respuesta
              {sugerencias.perfilAplicado && (
                <Badge className="bg-violet-50 text-violet-700 border-violet-200 text-[9px] border ml-1">Tu estilo</Badge>
              )}
              {sugerencias.contexto && sugerencias.contexto.vinculado && (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px] border ml-1">+ Contexto CRM</Badge>
              )}
              {sugerencias.memoriasRelevantes && sugerencias.memoriasRelevantes.length > 0 && (
                <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[9px] border ml-1" data-testid="badge-memorias-relevantes">
                  + {sugerencias.memoriasRelevantes.length} memoria{sugerencias.memoriasRelevantes.length === 1 ? '' : 's'}
                </Badge>
              )}
            </h3>

            {sugerencias.memoriasRelevantes && sugerencias.memoriasRelevantes.length > 0 && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded" data-testid="panel-memorias-relevantes">
                <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-1.5">Memorias relevantes del histórico (búsqueda vectorial)</div>
                <ul className="space-y-1.5">
                  {sugerencias.memoriasRelevantes.slice(0, 3).map((m, i) => (
                    <li key={i} className="text-[11px] text-blue-900" data-testid={`memoria-relevante-${i}`}>
                      <span className="font-medium">[{(m.received_at || '').slice(0, 10)}]</span>
                      {' '}<span className="text-blue-700">{m.from_address}</span>
                      {' · '}<em>"{m.subject || '(sin asunto)'}"</em>
                      <span className="text-blue-500 ml-1">· score {(m.score * 100).toFixed(0)}%</span>
                      <div className="text-blue-700/80 italic ml-2 line-clamp-1">↳ {m.snippet}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {sugerencias.loading ? (
              <div className="py-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-fuchsia-600 mx-auto mb-2" />
                <p className="text-sm text-ink-500">Gemini está generando borradores con tu estilo…</p>
              </div>
            ) : sugerencias.borradores.length === 0 ? (
              <div className="py-8 text-center text-ink-500 text-sm">Gemini no devolvió borradores. Inténtalo de nuevo.</div>
            ) : (
              <div className="space-y-2 mt-2">
                {sugerencias.borradores.map((b, i) => (
                  <div
                    key={i}
                    className="p-3 border border-ink-200 rounded hover:border-fuchsia-300 transition cursor-pointer bg-white"
                    onClick={() => usarBorrador(b)}
                    data-testid={`correos-borrador-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 text-[10px] border">Opción {i + 1}</Badge>
                      <span className="font-medium text-ink-900 text-sm">{b.titulo}</span>
                    </div>
                    <div className="text-xs text-ink-600 mb-1"><span className="font-semibold">Asunto:</span> {b.asunto}</div>
                    <pre className="text-xs text-ink-700 whitespace-pre-wrap font-sans line-clamp-6 bg-ink-50/50 rounded p-2 border border-ink-100">{b.cuerpo}</pre>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setSugerencias({ open: false, loading: false, msg: null, borradores: [], perfilAplicado: false, contexto: null, memoriasRelevantes: [] })}>Cerrar</Button>
            </div>
          </Card>
        </div>
      )}

      {tareasIA.open && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[55] p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) cerrarTareasIA(); }}
        >
          <Card className="max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="dialog-extraer-tareas-correos">
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" /> Tareas detectadas por Gemini
            </h3>
            <p className="text-xs text-ink-500 mb-3">Revisa, marca/desmarca y crea las que tengan sentido.</p>

            {tareasIA.msg && (
              <div className="bg-ink-50 border border-ink-200 rounded p-2 mb-3 text-xs">
                <div className="font-semibold text-ink-700 truncate">📧 {tareasIA.msg.subject || '(sin asunto)'}</div>
                <div className="text-ink-500 mt-0.5">De: {tareasIA.msg.from_name || tareasIA.msg.from_address}</div>
              </div>
            )}

            {tareasIA.loading && (
              <div className="py-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-violet-600 mx-auto mb-2" />
                <p className="text-sm text-ink-500">Gemini está leyendo el correo y extrayendo tareas…</p>
              </div>
            )}

            {!tareasIA.loading && tareasIA.resumen && (
              <div className="mb-3 p-3 bg-violet-50 border border-violet-200 rounded text-xs text-violet-900">
                <div className="font-semibold mb-1 uppercase tracking-wider text-[10px]">Resumen IA</div>
                {tareasIA.resumen}
              </div>
            )}

            {!tareasIA.loading && tareasIA.propuestas.length === 0 && (
              <div className="py-8 text-center text-ink-500 text-sm" data-testid="ia-sin-tareas-correos">
                Gemini no ha detectado tareas concretas en este correo.
              </div>
            )}

            {!tareasIA.loading && tareasIA.propuestas.length > 0 && (
              <div className="space-y-2" data-testid="ia-tareas-list-correos">
                {tareasIA.propuestas.map((t, i) => {
                  const sel = tareasIA.seleccionadas.has(i);
                  return (
                    <div
                      key={i}
                      onClick={() => toggleTareaSel(i)}
                      className={`p-3 border rounded cursor-pointer transition ${sel ? 'border-violet-400 bg-violet-50/50' : 'border-ink-200 bg-white hover:border-ink-300'}`}
                      data-testid={`ia-tarea-correos-${i}`}
                    >
                      <div className="flex items-start gap-2">
                        {sel ? <CheckSquare className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" /> : <Square className="w-4 h-4 text-ink-300 mt-0.5 shrink-0" />}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-ink-900">{t.titulo}</span>
                            <Badge className={`text-[9px] border ${
                              t.prioridad === 'alta' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                              t.prioridad === 'baja' ? 'bg-ink-50 text-ink-600 border-ink-200' :
                              'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>{t.prioridad}</Badge>
                            {t.categoria && <Badge className="text-[9px] bg-ink-50 text-ink-600 border-ink-200 border">{t.categoria}</Badge>}
                            {(t.fecha_propuesta || t.vencimiento) && <Badge className="text-[9px] bg-blue-50 text-blue-700 border-blue-200 border">📅 {t.fecha_propuesta || t.vencimiento}</Badge>}
                          </div>
                          {t.descripcion && <p className="text-xs text-ink-600 mt-1">{t.descripcion}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!tareasIA.loading && tareasIA.propuestas.length > 0 && (
              <div className="flex justify-between items-center mt-4 gap-2">
                <span className="text-xs text-ink-500">{tareasIA.seleccionadas.size} de {tareasIA.propuestas.length} seleccionadas</span>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={cerrarTareasIA}>Cancelar</Button>
                  <Button
                    onClick={crearTareasSeleccionadas}
                    disabled={tareasIA.seleccionadas.size === 0 || tareasIA.creando}
                    className="bg-violet-600 hover:bg-violet-700"
                    data-testid="ia-crear-tareas-correos"
                  >
                    {tareasIA.creando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando…</> : <><ListChecks className="w-4 h-4 mr-1.5" /> Crear seleccionadas</>}
                  </Button>
                </div>
              </div>
            )}

            {!tareasIA.loading && tareasIA.propuestas.length === 0 && (
              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={cerrarTareasIA}>Cerrar</Button>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
